/**
 * useVinylSubmit — React hook wrapping the vinyl record submit animation lifecycle.
 * Handles: validate -> start animation -> await mutation + min duration -> poof -> reset.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import {
  startVinylAnimation,
  triggerFinalPoof,
  resetModalStyles,
  type VinylAnimationRefs,
} from './VinylAnimation';
import { haptic } from '@/utils/haptics';
import { sound } from '@/utils/sounds';
import type { VinylSubmitConfig, SubmitWithAnimationConfig } from './types';

export function useVinylSubmit({ modalContentRef, vinylLabel = 'BRAINSWAY', notifyReportSubmitted = false }: VinylSubmitConfig) {
  const [isAnimating, setIsAnimating] = useState(false);
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const vinylOverlayRef = useRef<HTMLDivElement | null>(null);
  const spinTimelineRef = useRef<gsap.core.Timeline | null>(null);

  const refs: VinylAnimationRefs = { modalContentRef, vinylOverlayRef, spinTimelineRef };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetModalStyles(refs);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitWithAnimation = useCallback(
    async ({ validate, mutate, onSuccess, onError }: SubmitWithAnimationConfig) => {
      if (!validate()) return;

      try {
        setIsAnimating(true);
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!prefersReducedMotion && modalContentRef.current) {
          startVinylAnimation(refs, vinylLabel);
        }

        const mutatePromise = mutate();
        if (!prefersReducedMotion) {
          await Promise.all([mutatePromise, new Promise((r) => setTimeout(r, 2800))]);
        } else {
          await mutatePromise;
        }

        if (!prefersReducedMotion && modalContentRef.current) {
          triggerFinalPoof(refs, true, isMobile);
          // Close the modal immediately so React unmounts it — prevents flash
          // from re-renders during the wait. The helmet overlay lives in
          // document.body and survives the unmount.
          onSuccess();
          // Delay sound + notification until the fly animation lands
          // (400ms charge + 500–700ms flight ≈ 1100ms)
          setTimeout(() => {
            sound.success();
            if (notifyReportSubmitted) {
              window.dispatchEvent(new CustomEvent('report-submitted', { detail: {} }));
            }
          }, 1100);
          await new Promise((r) => setTimeout(r, 1200));
          resetModalStyles(refs);
        } else {
          resetModalStyles(refs);
          sound.success();
          onSuccess();
          if (notifyReportSubmitted) {
            window.dispatchEvent(new CustomEvent('report-submitted', { detail: {} }));
          }
        }
      } catch (err: any) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!prefersReducedMotion && modalContentRef.current) {
          triggerFinalPoof(refs, false, isMobile);
          setTimeout(() => sound.error(), 1100);
          await new Promise((r) => setTimeout(r, 1200));
          resetModalStyles(refs);
        } else {
          sound.error();
        }
        onError(err);
      } finally {
        setIsAnimating(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalContentRef, vinylLabel, isMobile],
  );

  /**
   * Batch submit: N helmets in polygon formation, each pops sequentially
   * as its mutation resolves, formation reforms after each pop.
   */
  const submitBatchWithAnimation = useCallback(
    async ({
      items,
      mutateOne,
      onAllDone,
      onError,
    }: {
      items: { formData: any }[];
      mutateOne: (formData: any) => Promise<unknown>;
      onAllDone: (succeeded: number, failed: number) => void;
      onError: (err: Error) => void;
    }) => {
      if (items.length === 0) return;

      try {
        setIsAnimating(true);
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Single helmet animation — same as solo submit
        if (!prefersReducedMotion && modalContentRef.current) {
          startVinylAnimation(refs, vinylLabel);
        }

        // Run all mutations while the helmet floats
        let succeeded = 0;
        let failed = 0;

        const results = await Promise.allSettled(
          items.map(async (item) => {
            await mutateOne(item.formData);
          }),
        );
        for (const r of results) {
          if (r.status === 'fulfilled') { succeeded++; }
          else { failed++; }
        }

        // Ensure minimum animation duration (2.8s same as single submit)
        if (!prefersReducedMotion) {
          await new Promise((r) => setTimeout(r, 2800));
        }

        // Single helmet poof
        if (!prefersReducedMotion && modalContentRef.current) {
          triggerFinalPoof(refs, failed === 0, isMobile);
          setTimeout(() => {
            if (succeeded > 0) haptic.success();
            sound.success();
            if (notifyReportSubmitted && succeeded > 0) {
              window.dispatchEvent(new CustomEvent('report-submitted', { detail: { count: succeeded } }));
            }
          }, 1100);
          onAllDone(succeeded, failed);
          await new Promise((r) => setTimeout(r, 1200));
          resetModalStyles(refs);
        } else {
          resetModalStyles(refs);
          if (notifyReportSubmitted && succeeded > 0) {
            window.dispatchEvent(new CustomEvent('report-submitted', { detail: { count: succeeded } }));
          }
          onAllDone(succeeded, failed);
        }
      } catch (err: any) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!prefersReducedMotion && modalContentRef.current) {
          triggerFinalPoof(refs, false, isMobile);
          await new Promise((r) => setTimeout(r, 1200));
          resetModalStyles(refs);
        }
        onError(err);
      } finally {
        setIsAnimating(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalContentRef, vinylLabel, isMobile, notifyReportSubmitted],
  );

  return { isAnimating, submitWithAnimation, submitBatchWithAnimation };
}
